import React from 'react';
import PropTypes from 'prop-types';
import compose from 'recompose/compose';
import styled from 'styled-components';
import theme from 'theme/theme';
import translate from 'components/hoc/translate';
import utils from 'helpers/utils';
import { BackupIcon } from 'assets/materialIcons/materialIcons';
import { connect } from 'react-redux';

const DropzoneDescription = styled.div`
    font-size: 10px;
    line-height: 1.4;
    font-style: italic;
    text-align: center;
`;

const DropzoneButton = styled.button`
    padding: 0 10px;
    display: flex;
    height: 33px;
    justify-content: center;
    align-items: center;
    background-color: ${ theme.palette.secondary };
    border: 2px solid ${ theme.palette.secondary };
    color: ${ theme.palette.white };
    border-radius: 4px;
    pointer-events: none;
    user-select: none;
    outline: none;

    span {
        margin-left: 5px;
    }
`;

const DropzoneInput = styled.input`
    position: absolute;
    top: 0;
    left: 0;
    width: 0.1px;
    height: 0.1px;
    opacity: 0;
    overflow: hidden;
    z-index: -1;
`;

const DropzoneLabel = styled.label`
    display: flex;
    height: 200px;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    border: 1px dashed ${ theme.palette.doveGray };
    cursor: pointer;
    user-select: none;
    ${ (props) => {
        if (!props.hover) {
            return;
        }

        return `
            background-color: ${ theme.palette.offWhite };
        `;
    } };

    p {
        margin-top: 5px;
    }

    svg {
        fill: ${ theme.palette.white }
    }

    &:hover {
        background-color: ${ theme.palette.offWhite };
    }

    ${ DropzoneInput }:focus & {
        border: 2px dashed ${ theme.palette.secondary };
        cursor: pointer;

        outline: 1px dotted #000;
        outline: -webkit-focus-ring-color auto 5px;
    }
`;

class FileDropzoneField extends React.Component {
    constructor() {
        super();

        this.fileInput = null;

        this.state = {
            hover: false,
        };
    }

    componentDidMount() {
        const { dropZoneId } = this.props;

        const dropZone = document.getElementById(dropZoneId);

        dropZone.addEventListener('dragend', this.onDragEnd);
        dropZone.addEventListener('dragover', this.onDragOver);
        dropZone.addEventListener('dragenter', this.onDragEnter);
        dropZone.addEventListener('dragleave', this.onDragLeave);
        dropZone.addEventListener('drop', this.onDrop);
    }

    componentWillUnmount() {
        const { dropZoneId } = this.props;

        const dropZone = document.getElementById(dropZoneId);

        dropZone.addEventListener('dragend', this.onDragEnd);
        dropZone.addEventListener('dragover', this.onDragOver);
        dropZone.addEventListener('dragenter', this.onDragEnter);
        dropZone.addEventListener('dragleave', this.onDragLeave);
        dropZone.addEventListener('drop', this.onDrop);
    }

    setFileInputRef = (ref) => {
        this.fileInput = ref;
    }

    fileInputClick = () => {
        return this.fileInput.click();
    }

    handleFiles = (files) => {
        const { formDataKey, multiple, upload } = this.props;

        if (utils.isEmpty(files)) {
            return;
        }

        if (multiple) {
            utils.each(files, (file) => {
                upload(file, formDataKey);
            });
        } else {
            upload(files[0], formDataKey);
        }
    }

    setHover = (state) => {
        this.setState({
            hover: state,
        });
    }

    onDragEnter = () => {
        if (!this.state.hover) {
            this.setHover(true);
        }
    }

    onDragEnd = () => {
        if (this.state.hover) {
            this.setHover(false);
        }
    }

    onDragLeave = () => {
        if (this.state.hover) {
            this.setHover(false);
        }
    }

    onDragOver = (event) => {
        event.preventDefault();

        if (!this.state.hover) {
            this.setHover(true);
        }
    }

    onDrop = (event) => {
        event.preventDefault();

        const dt = event.dataTransfer;
        const files = dt.files;

        this.handleFiles(files);

        this.onDragLeave;
    }

    render() {
        const {
            acceptedFormat,
            buttonText,
            description,
            dropZoneId,
            formDataKey,
            maxWidth,
            maxFileSize,
            maxHeight,
            minHeight,
            minWidth,
            multiple,
            translate,
        } = this.props;

        const {
            hover,
        } = this.state;

        return (
            <div>
                <DropzoneLabel
                    id={ dropZoneId }
                    htmlFor='file'
                    hover={ hover }
                    onClick={ (e) => {
                        return this.fileInputClick(e);
                    } }
                >
                    <DropzoneButton>
                        <BackupIcon color={ theme.palette.white } />

                        <span>{ buttonText ? buttonText : translate('dropzone.label.button') }</span>
                    </DropzoneButton>

                    <p>{ translate('dropzone.label.text') }</p>

                    <DropzoneDescription>
                        { description &&
                            <div>{ description }</div>
                        }

                        {
                            (formDataKey === 'image' || formDataKey === 'asset') && (
                                (minHeight && minWidth) ?
                                    <div>{ translate('dropzone.label.description_min', { height: minHeight, width: minWidth }) }</div>
                                    :
                                    <div>{ translate('dropzone.label.description_max', { height: maxHeight, width: maxWidth }) }</div>
                            )
                        }

                        {
                            maxFileSize &&
                                <div>{ translate('dropzone.label.description_file_max', { max: maxFileSize }) }</div>
                        }

                        <div>{ translate('dropzone.label.format', { format: acceptedFormat }) }</div>
                    </DropzoneDescription>
                </DropzoneLabel>

                <DropzoneInput
                    accept={ acceptedFormat }
                    name='file'
                    multiple={ multiple }
                    innerRef={ this.setFileInputRef }
                    onChange={ (event) => {
                        const { files } = event.target;

                        this.handleFiles(files);
                    } }
                    type='file'
                />
            </div>
        );
    }
}

FileDropzoneField.propTypes = {
    acceptedFormat: PropTypes.string.isRequired,
    buttonText: PropTypes.string,
    description: PropTypes.string,
    dropZoneId: PropTypes.string.isRequired,
    formDataKey: PropTypes.string.isRequired,
    maxFileSize: PropTypes.number,
    maxHeight: PropTypes.number,
    maxWidth: PropTypes.number,
    minHeight: PropTypes.number,
    minWidth: PropTypes.number,
    multiple: PropTypes.bool,
    translate: PropTypes.func.isRequired,
    upload: PropTypes.func.isRequired,
};

FileDropzoneField.defaultProps = {
    multiple: false,
};

const mapDispatchToProps = (dispatch, props) => {
    return {
        upload: (file, formDataKey) => {
            const {
                createAction,
                id,
                onError,
                onSuccess,
                setLoadingOn,
            } = props;

            const formData = new FormData();

            formData.append(formDataKey, file);

            if (setLoadingOn) {
                setLoadingOn();
            }

            dispatch(createAction({
                formData: formData,
                id: id,
                onError: onError,
                onSuccess: onSuccess,
            }));
        },
    };
};

const enhance = compose(
    connect(
        null,
        mapDispatchToProps,
    ),
    translate,
);

export default enhance(FileDropzoneField);



// WEBPACK FOOTER //
// ./src/components/input/Dropzone/FileDropzoneField.js